import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { BehaviorSubject, catchError, filter, finalize, Observable, switchMap, take, throwError } from 'rxjs';
import { Injectable } from '@angular/core';
import { UtilsService } from './utils.service';
import { AuthService } from '../auth/auth.service';

@Injectable()
export class GeneralInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private readonly tokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private readonly authService: AuthService,
    private readonly utils: UtilsService) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const apiUrl = this.utils.getAPIServerUrl();

    if (req.url.indexOf('images') > -1) {
      return next.handle(req);
    }

    const url = req.url.startsWith('http') ? req.url : `${apiUrl}${req.url}`;
    const token = this.authService.getToken();

    if (req.url.includes('/api/v1/auth/login') || req.url.includes('/api/v1/auth/refresh')) {
      return next.handle(req);
    }

    const clonedReq = token ? this.addAuthHeader(req.clone({url}), token) : req.clone({ url });

    return next.handle(clonedReq).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse){
          if(error.status === 401) {
            return this.handle401(clonedReq, next);
          } else if (error.status === 400) {
            this.authService.logout(); 
          }
        }
        return throwError(() => error);
      })
    );
  }

  private handle401(req: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.tokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap((res: any) => {
          const newAccess = res.data.accessToken;
          if (!newAccess) {
            this.authService.logout(false);
            return throwError(() => new Error("No access token returned"));
          }

          const user = JSON.parse(localStorage.getItem('user') || '{}');
          user.accessToken = newAccess;
          localStorage.setItem('user', JSON.stringify(user));
          
          this.tokenSubject.next(newAccess);
          return next.handle(this.addAuthHeader(req, newAccess));
        }),

        catchError(err => {
          this.authService.logout(false);
          return throwError(() => err);
        }),

        finalize(() => {
          this.isRefreshing = false;
        })
      );
    }

    return this.tokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => next.handle(this.addAuthHeader(req, token!)))
    );
  }

  private addAuthHeader(req: HttpRequest<any>, token: string): HttpRequest<any> {
    const headersConfig: any = {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Accept-Language': 'en', 
      'ngrok-skip-browser-warning': 'true'
    };

    if (!(req.body instanceof FormData)) {
      headersConfig['Content-Type'] = 'application/json';
    }

    return req.clone({
      setHeaders: headersConfig
    });
  }
}
